// Import webrtc-adapter to normalize WebRTC behavior across browsers (Safari/Chrome/Firefox)
import 'webrtc-adapter';
import Peer, { DataConnection } from 'peerjs';
import type { NetworkMessage, ConnectionStatus, NetworkEventHandlers } from './NetworkTypes';

/**
 * Generates a random 6-character alphanumeric room code
 */
function generateRoomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed confusing chars (I, O, 0, 1)
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// PeerJS configuration - keep it simple for cross-browser compatibility
const PEER_CONFIG: import('peerjs').PeerOptions = {
  debug: 2, // Log errors and warnings
};

/**
 * PeerManager - Wrapper for PeerJS WebRTC connections
 *
 * Handles peer-to-peer connection setup and message passing between
 * host and guest players for online multiplayer.
 */
export class PeerManager {
  private peer: Peer | null = null;
  private connection: DataConnection | null = null;
  private handlers: NetworkEventHandlers = {};
  private _status: ConnectionStatus = 'disconnected';
  private _roomId: string | null = null;

  /**
   * Current connection status
   */
  get status(): ConnectionStatus {
    return this._status;
  }

  /**
   * Current room ID (only set for host)
   */
  get roomId(): string | null {
    return this._roomId;
  }

  /**
   * Set event handlers
   */
  setHandlers(handlers: NetworkEventHandlers): void {
    this.handlers = handlers;
  }

  /**
   * Create a new room as host
   * @returns Promise that resolves with the room code
   */
  async createRoom(): Promise<string> {
    return new Promise((resolve, reject) => {
      this._status = 'connecting';
      this._roomId = generateRoomCode();

      // Create peer with room code as ID
      this.peer = new Peer(this._roomId, PEER_CONFIG);

      this.peer.on('open', (id) => {
        console.log('[PeerManager] Host room created:', id);
        resolve(id);
      });

      this.peer.on('connection', (conn) => {
        console.log('[PeerManager] Guest connecting, conn.open:', conn.open, 'type:', conn.type);
        this.connection = conn;
        this.setupConnectionHandlers(conn);
      });

      this.peer.on('error', (err) => {
        console.error('[PeerManager] Peer error:', err);
        this._status = 'error';
        this.handlers.onError?.(err);
        reject(err);
      });

      this.peer.on('disconnected', () => {
        console.log('[PeerManager] Peer disconnected from server');
      });
    });
  }

  /**
   * Join an existing room as guest
   * @param roomId The room code to join
   */
  async joinRoom(roomId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this._status = 'connecting';
      const normalizedRoomId = roomId.toUpperCase().trim();

      // Create peer with random ID (guest doesn't need specific ID)
      this.peer = new Peer(PEER_CONFIG);

      this.peer.on('open', (peerId) => {
        console.log('[PeerManager] Guest peer created with ID:', peerId, 'connecting to host:', normalizedRoomId);

        // Connect to the host
        const conn = this.peer!.connect(normalizedRoomId, {
          reliable: true,
        });

        this.connection = conn;

        conn.on('open', () => {
          console.log('[PeerManager] Connected to host');
          this._status = 'connected';
          this.handlers.onConnected?.();
          this.setupConnectionHandlers(conn);
          resolve();
        });

        conn.on('error', (err) => {
          console.error('[PeerManager] Connection error:', err);
          this._status = 'error';
          this.handlers.onError?.(err);
          reject(err);
        });
      });

      this.peer.on('error', (err) => {
        console.error('[PeerManager] Peer error:', err);
        this._status = 'error';
        this.handlers.onError?.(err);
        reject(err);
      });

      // Timeout after 15 seconds (increased for slower NAT traversal)
      setTimeout(() => {
        if (this._status === 'connecting') {
          const error = new Error('Connection timeout - room may not exist');
          this._status = 'error';
          this.handlers.onError?.(error);
          reject(error);
        }
      }, 15000);
    });
  }

  /**
   * Setup handlers for a data connection
   */
  private setupConnectionHandlers(conn: DataConnection): void {
    // Check if connection is already open (can happen on host side)
    if (conn.open) {
      console.log('[PeerManager] Connection already open');
      this._status = 'connected';
      this.handlers.onConnected?.();
    } else {
      conn.on('open', () => {
        console.log('[PeerManager] Connection opened');
        this._status = 'connected';
        this.handlers.onConnected?.();
      });
    }

    conn.on('data', (data) => {
      console.log('[PeerManager] Received message:', data);
      if (this.handlers.onMessage && this.isValidMessage(data)) {
        this.handlers.onMessage(data as NetworkMessage);
      }
    });

    conn.on('close', () => {
      console.log('[PeerManager] Connection closed');
      this._status = 'disconnected';
      this.connection = null;
      this.handlers.onDisconnected?.();
    });

    conn.on('error', (err) => {
      console.error('[PeerManager] Connection error:', err);
      this._status = 'error';
      this.handlers.onError?.(err);
    });
  }

  /**
   * Validate that received data is a valid NetworkMessage
   */
  private isValidMessage(data: unknown): boolean {
    if (typeof data !== 'object' || data === null) return false;
    const msg = data as Record<string, unknown>;
    return typeof msg.type === 'string';
  }

  /**
   * Send a message to the connected peer
   */
  send(message: NetworkMessage): void {
    if (!this.connection || this.connection.open === false) {
      console.warn('[PeerManager] Cannot send - no active connection');
      return;
    }
    console.log('[PeerManager] Sending message:', message);
    this.connection.send(message);
  }

  /**
   * Check if connected to a peer
   */
  isConnected(): boolean {
    return this._status === 'connected' && this.connection !== null;
  }

  /**
   * Disconnect and clean up
   */
  disconnect(): void {
    console.log('[PeerManager] Disconnecting');

    if (this.connection) {
      this.connection.close();
      this.connection = null;
    }

    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }

    this._status = 'disconnected';
    this._roomId = null;
  }
}

// Singleton instance
let instance: PeerManager | null = null;

export function getPeerManager(): PeerManager {
  if (!instance) {
    instance = new PeerManager();
  }
  return instance;
}

export function resetPeerManager(): void {
  if (instance) {
    instance.disconnect();
    instance = null;
  }
}
